import { createFileRoute, Outlet, Link, useLocation } from '@tanstack/react-router';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { openPreparedPreview } from '@/utils/desktopActions';
import {
  siteKeys,
  sitesApi,
  useAuthStore,
  useSite,
  useSiteSettings,
  useSites,
} from '@wordpuppi/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { resolveSiteAssetUrl } from '@/lib/siteAssets';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { sidebarDomain, sidebarNoticePosition, sidebarNoticeOutline, useBaxterEffects, type SidebarNotice } from '@/lib/baxterEffects';
import type { Domain } from '@/components/baxter/toolEffects';
import { History, Plus, PanelLeftClose, Globe, LogOut, UserRound, ChevronRight, ExternalLink, Menu, Newspaper, Files, Images, Palette, ClipboardList, Megaphone, Users, BarChart3, Shapes, Table2, SlidersHorizontal, Sparkles, Database, Mail, Plug, Settings2, Languages } from 'lucide-react';
import { ThemeToggle } from '@/components/ThemeToggle';
import { AccountMenu } from '@/components/AccountMenu';
import { DeployBadge } from '@/components/DeployBadge';
import { BaxterPanel, type BaxterDock } from '@/components/baxter/BaxterPanel';
import { BaxterMark } from '@/components/baxter/BaxterMark';
import { BAXTER_BRIEF_EVENT, hasBaxterBrief } from '@/components/baxter/baxterBrief';
import { BaxterMagic } from '@/components/baxter/BaxterMagic';
import { clearMagic, peekMagic } from '@/components/baxter/magic';
import logoBlack from '@/assets/logo-black-300w.webp';
import logoWhite from '@/assets/logo-white-300w.webp';

export const Route = createFileRoute('/sites/$siteSlug')({
  component: SiteLayout,
});

// Full-width sidebar nav item. Active items carry the amber-dot marker + raised
// surface (the banned side-stripe is replaced by NavDot + bg-sidebar-accent).
const navItemClass = (active: boolean, notice?: SidebarNotice[]) =>
  cn(
    'flex items-center gap-2 px-4 py-2.5 text-sidebar-muted rounded-md outline-none transition-colors motion-reduce:transition-none hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
    active && 'bg-sidebar-accent text-sidebar-foreground font-medium',
    !active && notice && 'text-sidebar-foreground'
  );

const subItemClass = (active: boolean) =>
  cn(
    'flex items-center gap-2 px-4 py-2 text-sm text-sidebar-muted rounded-md outline-none transition-colors motion-reduce:transition-none hover:bg-sidebar-accent hover:text-sidebar-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50',
    active && 'bg-sidebar-accent text-sidebar-foreground font-medium'
  );

// AB#615 nit B: the sidebar nav scrolls (overflow-y-auto), but Settings and
// Custom Types are its LAST items — on laptop-height viewports their sub-items
// expand below the fold, and macOS overlay scrollbars give no hint they exist.
// A submenu's ref fires once on expand; `block: 'nearest'` is a no-op when it
// is already visible. Module scope keeps the identity stable so React doesn't
// re-run it every render.
const revealSubmenu = (el: HTMLDivElement | null) => el?.scrollIntoView({ block: 'nearest' });

// Amber marker for the active nav item; renders an invisible placeholder when
// inactive so labels never shift horizontally.
function NavDot({ active }: { active: boolean }) {
  return (
    <span className={cn('sidebar-active-dot size-1.5 shrink-0 rounded-full bg-primary', !active && 'invisible')} />
  );
}

function NoticeCard({ item, fade = true }: { item: SidebarNotice; fade?: boolean }) {
  const [elapsed] = useState(() => Math.max(0, Date.now() - item.createdAt));
  return <span className={cn("px-3 py-2 text-xs font-normal leading-relaxed text-popover-foreground break-words", fade && "baxter-nav-notice")}
    style={{ animationDuration: `${item.expiresAt - item.createdAt}ms`, animationDelay: `-${elapsed}ms` }}>{item.text}</span>;
}

function NoticeOutlineFade({ item, children }: { item?: SidebarNotice; children: ReactNode }) {
  const [elapsed] = useState(() => item ? Math.max(0, Date.now() - item.createdAt) : 0);
  return <div data-sidebar-notice className={cn('pointer-events-none fixed inset-0 z-[100]', item && 'baxter-nav-notice')}
    style={item ? { animationDuration: `${item.expiresAt - item.createdAt}ms`, animationDelay: `-${elapsed}ms` } : undefined}>
    {children}
  </div>;
}

function NavLabel({ label, notice, sidebarOpen }: { label: string; notice?: SidebarNotice[]; sidebarOpen: boolean }) {
  const anchor = useRef<HTMLSpanElement>(null);
  const stack = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number; width: number; height: number; itemLeft: number; itemTop: number; itemBottom: number; itemRight: number; itemHeight: number } | null>(null);
  useLayoutEffect(() => {
    if (!notice?.length) return;
    const place = () => {
      const item = anchor.current?.closest('a') ?? anchor.current;
      if (!item) return;
      const rect = item.getBoundingClientRect();
      // A closed mobile drawer has no visible anchor: never float notices over unrelated content.
      if ((!sidebarOpen && innerWidth < 768) || !item.getClientRects().length || rect.right <= 0 || rect.top >= innerHeight || rect.bottom <= 0) {
        setPosition(null);
        return;
      }
      const height = Math.max(rect.height, stack.current?.getBoundingClientRect().height ?? rect.height);
      const { top, left, width } = sidebarNoticePosition(rect, anchor.current!.getBoundingClientRect().right,
        { width: innerWidth, height: innerHeight }, height);
      const next = { top, left, width, height, itemLeft: rect.left, itemTop: rect.top,
        itemBottom: rect.bottom, itemRight: rect.right, itemHeight: rect.height };
      setPosition((previous) => JSON.stringify(previous) === JSON.stringify(next) ? previous : next);
    };
    place();
    const drawer = anchor.current?.closest('aside');
    drawer?.addEventListener('transitionend', place);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    const observer = new ResizeObserver(place);
    if (anchor.current) observer.observe(anchor.current);
    if (stack.current) observer.observe(stack.current);
    return () => {
      observer.disconnect();
      drawer?.removeEventListener('transitionend', place);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [notice, sidebarOpen, position?.width]);
  return <span ref={anchor} className="min-w-0">
    <span className="sidebar-label block">{label}</span>
    {notice?.length && position ? createPortal(<NoticeOutlineFade key={notice.length === 1 ? notice[0].id : 'stack'} item={notice.length === 1 ? notice[0] : undefined}>
      {/* Paint over the sidebar edge only in the attachment gap; never cover its label. */}
      {position.left > position.itemRight && <div aria-hidden="true" className="pointer-events-none fixed z-[100] bg-popover"
        style={{ left: position.itemRight, top: position.itemTop, width: position.left - position.itemRight, height: position.itemHeight }} />}
      <div ref={stack} aria-hidden="true" className="pointer-events-none fixed z-[100] flex flex-col justify-center rounded-r-md bg-popover"
        style={{ top: position.top, left: position.left, width: position.width, minHeight: position.itemHeight }}>
        {notice.map((item) => <NoticeCard key={item.id} item={item} fade={notice.length > 1} />)}
      </div>
      <svg aria-hidden="true" className="pointer-events-none fixed inset-0 z-[100] h-full w-full overflow-visible fill-none stroke-primary/50">
        <path d={sidebarNoticeOutline({ left: position.itemLeft, top: position.itemTop, bottom: position.itemBottom }, position)}
          strokeWidth="1" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      </svg>
    </NoticeOutlineFade>, document.body) : null}
  </span>;
}

function SiteLayout() {
  const { siteSlug } = Route.useParams();
  const location = useLocation();
  const currentDomain = sidebarDomain(location.pathname, siteSlug);
  const notices = useBaxterEffects((s) => s.notices);
  const noticeSiteSlug = useBaxterEffects((s) => s.noticeSiteSlug);
  const announcement = useBaxterEffects((s) => s.announcement);
  const visit = useBaxterEffects((s) => s.visit);
  const expireNotices = useBaxterEffects((s) => s.expireNotices);
  useEffect(() => () => {
    if (useBaxterEffects.getState().noticeSiteSlug === siteSlug) visit('', null);
  }, [siteSlug, visit]);
  useEffect(() => {
    const deadline = Math.min(...Object.values(notices).flat().map((n) => n.expiresAt));
    if (!Number.isFinite(deadline)) return;
    const timer = setTimeout(() => expireNotices(Date.now()), Math.max(0, deadline - Date.now()));
    return () => clearTimeout(timer);
  }, [notices, expireNotices]);
  // Acknowledge before paint, including editor/subpage navigation. Incoming
  // changes to this section are also suppressed by mark(), even without navigation.
  useLayoutEffect(() => { visit(siteSlug, currentDomain); }, [siteSlug, currentDomain, visit]);
  const noticeForSection = (domain: Domain) =>
    noticeSiteSlug === siteSlug && domain !== currentDomain ? notices[domain] : undefined;
  const unreadCount = noticeSiteSlug === siteSlug
    ? Object.keys(notices).filter((domain) => domain !== currentDomain).length : 0;
  // Auth check handled by parent route (sites.tsx)
  const { user, logout } = useAuthStore();

  // Mobile: the sidebar is an off-canvas drawer (hidden < md). Close it on every
  // navigation so tapping a nav item doesn't leave the drawer covering the page.
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('wpp-sidebar-collapsed') === '1');
  // Desktop rail move in flight: the aside slides first and the rail layout lands
  // once its width transition ends (index.css, "Rail move").
  const [sidebarMoving, setSidebarMoving] = useState<'collapse' | 'expand'>();
  const sidebarRef = useRef<HTMLElement>(null);
  const toggleSidebar = () => {
    const next = !sidebarCollapsed;
    localStorage.setItem('wpp-sidebar-collapsed', next ? '1' : '0');
    setSidebarCollapsed(next);
    setSidebarMoving(next ? 'collapse' : 'expand');
  };
  useEffect(() => {
    if (!sidebarMoving) return;
    let live = true;
    const settle = () => { if (live) setSidebarMoving(undefined); };
    // No width transition (Reduce Motion, below md, jsdom) settles at once; a
    // reversal mid-move cancels this one and the next run waits on the new one.
    const moves = sidebarRef.current?.getAnimations?.().filter((a) => (a as CSSTransition).transitionProperty === 'width') ?? [];
    Promise.all(moves.map((a) => a.finished)).then(settle, settle);
    return () => { live = false; };
  }, [sidebarMoving]);
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Baxter (AB#645) lives in a right rail on md+, a drawer below it. Closed by
  // default; the choice sticks so a user who works with it never re-opens it.
  // A New Site wizard brief (AB#642) opens the rail on arrival: the panel is
  // unmounted while closed, and it is the thing that consumes the brief.
  const [baxterOpen, setBaxterOpen] = useState(
    () => localStorage.getItem('wpp-baxter-open') === '1' || hasBaxterBrief(siteSlug) || new URLSearchParams(window.location.search).has('sharedTemplate'),
  );
  // The New Site wizard's loader ("Baxter's magic"): shown once, over the site,
  // while the rail underneath runs the brief. Cleared on mount so a reload lands on the site.
  const [magic, setMagic] = useState(() => peekMagic(siteSlug));
  useEffect(() => { clearMagic(siteSlug); }, [siteSlug]);
  const toggleBaxter = (open: boolean) => {
    localStorage.setItem('wpp-baxter-open', open ? '1' : '0');
    setBaxterOpen(open);
    if (!open) setRailAction(undefined);
  };
  // A brief written while the shell is already mounted (the canvas ask, AB#810)
  // opens the rail the same way — the dock preference is untouched.
  useEffect(() => {
    const onBrief = (event: Event) => { if ((event as CustomEvent).detail === siteSlug) toggleBaxter(true); };
    window.addEventListener(BAXTER_BRIEF_EVENT, onBrief);
    return () => window.removeEventListener(BAXTER_BRIEF_EVENT, onBrief);
  }, [siteSlug]);
  // Which edge Baxter docks to on md+. Sticks like the open/closed choice does.
  const [baxterDock, setBaxterDock] = useState<BaxterDock>(() =>
    localStorage.getItem('wpp-baxter-dock') === 'bottom' ? 'bottom' : 'right',
  );
  const toggleBaxterDock = () => {
    const next: BaxterDock = baxterDock === 'right' ? 'bottom' : 'right';
    localStorage.setItem('wpp-baxter-dock', next);
    setBaxterDock(next);
  };
  const sizeKey = `wpp-baxter-size:${user?.id ?? 'unknown'}`;
  const collapsedKey = `wpp-baxter-collapsed:${user?.id ?? 'unknown'}`;
  const [baxterSize, setBaxterSize] = useState(() => {
    try { return (JSON.parse(localStorage.getItem(sizeKey) || '{}') ?? {}) as { right?: number; bottom?: number }; }
    catch { return {} as { right?: number; bottom?: number }; }
  });
  const [baxterCollapsed, setBaxterCollapsed] = useState(() => {
    if (new URLSearchParams(window.location.search).has('sharedTemplate')) return false;
    try { return localStorage.getItem(collapsedKey) === '1'; } catch { return false; }
  });
  const [railAction, setRailAction] = useState<{ kind: 'new' | 'history'; siteSlug: string }>();
  // The panel remounts per site and replays railAction on mount, so a click
  // made on site A must not survive a trip to B and back (it'd wipe A's chat).
  if (railAction && railAction.siteSlug !== siteSlug) setRailAction(undefined);
  const dragStart = useRef<{ position: number; size: number } | null>(null);
  const maxSize = baxterDock === 'right' ? Math.max(320, Math.min(640, window.innerWidth - 280)) : Math.max(240, Math.min(640, window.innerHeight - 80));
  const storedSize = baxterDock === 'right' ? baxterSize.right : baxterSize.bottom;
  const size = typeof storedSize === 'number' && Number.isFinite(storedSize)
    ? Math.max(baxterDock === 'right' ? 320 : 240, Math.min(maxSize, storedSize)) : Math.min(maxSize, 384);
  const resize = (next: number) => {
    const value = Math.round(Math.max(baxterDock === 'right' ? 320 : 240, Math.min(maxSize, next)));
    setBaxterSize((current) => {
      const updated = { ...current, [baxterDock]: value };
      try { localStorage.setItem(sizeKey, JSON.stringify(updated)); } catch { /* storage unavailable: keep the in-memory size */ }
      return updated;
    });
  };
  const collapseBaxter = (collapsed: boolean) => {
    try { localStorage.setItem(collapsedKey, collapsed ? '1' : '0'); } catch { /* storage unavailable: keep the in-memory state */ }
    setBaxterCollapsed(collapsed);
  };
  const runRailAction = (kind: 'new' | 'history') => {
    collapseBaxter(false);
    setRailAction({ kind, siteSlug });
  };
  const panelSpace = baxterOpen ? (baxterCollapsed ? 56 : size) : 0;
  const panelStyle = {
    '--baxter-width': `${baxterDock === 'right' ? panelSpace : size}px`,
    '--baxter-height': `${baxterDock === 'bottom' ? panelSpace : size}px`,
  } as CSSProperties;

  // Check if we're on a custom-types route to auto-expand the menu
  const isCustomTypesRoute = location.pathname.includes('/content-types');
  // AB#842: Versions lives under Settings, so its route keeps the sub-menu open.
  const isSettingsRoute = location.pathname.includes('/settings') || location.pathname.includes('/versions');
  const { data: siteData, isLoading, error } = useSite(siteSlug);
  const { data: sitesData } = useSites();

  // #623: publish the selected site (name + resolved favicon) to localStorage
  // for the desktop shell's titlebar badge — shell and admin iframe are
  // same-origin, so the shell hears the 'storage' event. Favicon saves route
  // through useUpdateSiteSettings, which invalidates this settings query, so
  // the badge updates live with zero code in the save path. Inert on the web
  // admin (nothing reads the key there).
  const { data: settingsData } = useSiteSettings(siteSlug);
  const siteName = siteData?.data?.name;

  // An explicit preview builds saved content first, including newly added pages.
  const queryClient = useQueryClient();
  const previewUrl = siteData?.data?.preview_url;
  const [previewBuilding, setPreviewBuilding] = useState(false);
  const previewInFlight = useRef(false);
  const openPreview = async () => {
    if (previewInFlight.current) return;
    if (!previewUrl) { toast.error('No preview link for this site yet'); return; }
    previewInFlight.current = true;
    setPreviewBuilding(true);
    try {
      await openPreparedPreview(async () => {
        const result = await sitesApi.build(siteSlug, {});
        if (!result.success || !result.data?.success) throw new Error(result.data?.message ?? 'Build failed');
        void queryClient.invalidateQueries({ queryKey: siteKeys.detail(siteSlug) });
        void queryClient.invalidateQueries({ queryKey: siteKeys.settings(siteSlug) });
        return result.data.preview_url ?? previewUrl;
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Build failed');
    } finally {
      previewInFlight.current = false;
      setPreviewBuilding(false);
    }
  };
  const badgeFavicon = settingsData?.data
    ? resolveSiteAssetUrl(settingsData.data.favicon_url, settingsData.data.url)
    : null;
  useEffect(() => {
    if (!siteName) return;
    localStorage.setItem(
      'wpp-current-site',
      JSON.stringify({ slug: siteSlug, name: siteName, favicon: badgeFavicon }),
    );
  }, [siteSlug, siteName, badgeFavicon]);
  // No site selected (back to /sites, account, login) -> clear the badge.
  useEffect(() => () => localStorage.removeItem('wpp-current-site'), []);

  if (isLoading) {
    return (
      <div className="flex min-h-screen">
        <aside className="w-60 bg-sidebar text-sidebar-foreground hidden md:flex flex-col fixed inset-y-0 left-0 border-r border-sidebar-border">
          <div className="p-4 border-b border-sidebar-border">
            <img src={logoBlack} alt="WordPuppi" className="dark:hidden" style={{ height: 'calc(var(--spacing) * 14)' }} />
            <img src={logoWhite} alt="WordPuppi" className="hidden dark:block" style={{ height: 'calc(var(--spacing) * 14)' }} />
          </div>
          <div className="p-4">
            <Skeleton className="h-8 w-full" />
          </div>
        </aside>
        <main className="flex-1 min-w-0 md:ml-60 p-6 bg-background min-h-screen">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </main>
      </div>
    );
  }

  if (error || !siteData?.data) {
    return (
      <div className="flex min-h-screen">
        <main className="flex-1 min-w-0 p-6">
          <div className="text-center py-12">
            <h2 className="text-xl font-semibold mb-2">Site not found</h2>
            <p className="text-muted-foreground mb-4">
              The site "{siteSlug}" doesn't exist or you don't have access.
            </p>
            <Button asChild>
              <Link to="/sites">Back to Sites</Link>
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const site = siteData.data;
  const sites = sitesData?.data || [];

  const navItems = [
    // AB#930: every glyph animates on hover, in CSS (see index.css); the per-path
    // ones ride on lucide's child order, pinned by navIconAnim.test.tsx.
    { to: '/sites/$siteSlug/posts', label: 'Posts', icon: Newspaper, anim: 'shimmer' },
    { to: '/sites/$siteSlug/pages', label: 'Pages', icon: Files, anim: 'shuffle' },
    { to: '/sites/$siteSlug/media', label: 'Media', icon: Images, anim: 'slide' },
    { to: '/sites/$siteSlug/theme', label: 'Theme', icon: Palette, anim: 'spin' },
    { to: '/sites/$siteSlug/forms', label: 'Forms', icon: ClipboardList, anim: 'tick' },
    { to: '/sites/$siteSlug/bark', label: 'Bark', icon: Megaphone, anim: 'shout' },
    { to: '/sites/$siteSlug/members', label: 'Members', icon: Users, anim: 'bob' },
    { to: '/sites/$siteSlug/search', label: 'Search', icon: BarChart3, anim: 'bars' },
  ];

  const customTypesItems = [
    { to: '/sites/$siteSlug/content-types', label: 'Types', icon: Shapes, exact: true },
    { to: '/sites/$siteSlug/content-types/tables', label: 'Tables', icon: Table2 },
  ];

  const settingsSubItems = [
    { to: '/sites/$siteSlug/settings', label: 'General', icon: SlidersHorizontal, exact: true },
    // AB#929: Baxter's magic sparkles read as solid, here and on the rail button.
    { to: '/sites/$siteSlug/settings/generation', label: 'Generation', icon: Sparkles, fill: true },
    { to: '/sites/$siteSlug/settings/databases', label: 'Databases', icon: Database },
    { to: '/sites/$siteSlug/settings/email', label: 'Email', icon: Mail },
    { to: '/sites/$siteSlug/settings/languages', label: 'Languages', icon: Languages },
    { to: '/sites/$siteSlug/versions', label: 'Versions', icon: History },
  ];

  return (
    <div className="site-layout flex min-h-screen" data-sidebar-collapsed={sidebarCollapsed && sidebarMoving !== 'collapse'} data-sidebar-moving={sidebarMoving}>
      <span role="status" aria-live="polite" aria-atomic="true" className="sr-only">
        {noticeSiteSlug === siteSlug && announcement && <span key={announcement.at}>{announcement.text}</span>}
      </span>
      {magic && <BaxterMagic siteSlug={siteSlug} request={magic} onClose={() => setMagic(null)} />}
      {/* Mobile top bar (< md): the only always-visible chrome; opens the drawer. */}
      <header className="md:hidden fixed top-0 inset-x-0 z-30 flex items-center gap-2 h-14 px-2 bg-sidebar border-b border-sidebar-border">
        <button
          type="button"
          aria-label={unreadCount ? `Open navigation menu, ${unreadCount} updated sections` : 'Open navigation menu'}
          aria-expanded={sidebarOpen}
          onClick={() => setSidebarOpen(true)}
          className="inline-flex items-center justify-center size-11 rounded-md text-sidebar-foreground outline-none transition-colors motion-reduce:transition-none hover:bg-sidebar-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <Menu className="size-5" />
          {unreadCount > 0 && <span aria-hidden="true" className="ml-1 size-1.5 rounded-full bg-primary" />}
        </button>
        <Link to="/sites" className="no-underline">
          <img src={logoBlack} alt="WordPuppi" className="dark:hidden h-7" />
          <img src={logoWhite} alt="WordPuppi" className="hidden dark:block h-7" />
        </Link>
        <button
          type="button"
          aria-label={previewBuilding ? "Building preview" : "Preview site"}
          aria-busy={previewBuilding}
          disabled={previewBuilding}
          onClick={openPreview}
          className="ml-auto inline-flex items-center justify-center size-11 rounded-md text-sidebar-foreground outline-none transition-colors motion-reduce:transition-none hover:bg-sidebar-accent focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <ExternalLink data-anim="nudge" className="size-5" />
        </button>
        <button
          type="button"
          aria-label={baxterOpen ? 'Close Baxter' : 'Open Baxter, your AI co-editor'}
          aria-expanded={baxterOpen}
          onClick={() => toggleBaxter(!baxterOpen)}
          className={cn(
            'inline-flex items-center justify-center size-11 rounded-md text-sidebar-foreground outline-none transition-colors motion-reduce:transition-none hover:bg-sidebar-accent focus-visible:ring-[3px] focus-visible:ring-ring/50',
            baxterOpen && 'bg-sidebar-accent',
          )}
        >
          <BaxterMark className="size-6" />
        </button>
      </header>

      {/* Drawer backdrop (< md). Stays mounted so it fades out with the drawer instead of vanishing under it. */}
      <div
        className={cn(
          'md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 ease-out',
          !sidebarOpen && 'pointer-events-none opacity-0',
        )}
        aria-hidden
        onClick={() => setSidebarOpen(false)}
      />

      <aside
        ref={sidebarRef}
        className={cn(
          'site-sidebar w-60 bg-sidebar text-sidebar-foreground flex flex-col fixed inset-y-0 left-0 z-50 border-r border-sidebar-border transition-transform duration-200 ease-drawer motion-reduce:transition-none md:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="sidebar-brand flex items-center gap-2 p-4 border-b border-sidebar-border">
          {/* AB#943: top of the side menu (was bottom-left), square, left of the logo. One glyph: the
              chevron turns to point the way the panel will go (index.css). */}
          <button type="button" onClick={toggleSidebar} aria-label={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'} title={sidebarCollapsed ? 'Expand navigation' : 'Collapse navigation'} aria-expanded={!sidebarCollapsed} aria-controls="site-navigation" className="sidebar-collapse hidden md:flex size-9 shrink-0 items-center justify-center rounded-md text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50">
            <PanelLeftClose data-anim="panel-toggle" className="size-5" />
          </button>
          <Link
            to="/sites"
            title="All sites"
            className="sidebar-brand-link min-w-0 flex-1 no-underline block rounded-md outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <img src={logoBlack} alt="WordPuppi" className="dark:hidden h-auto w-full" style={{ maxHeight: 'calc(var(--spacing) * 14)' }} />
            <img src={logoWhite} alt="WordPuppi" className="hidden dark:block h-auto w-full" style={{ maxHeight: 'calc(var(--spacing) * 14)' }} />
          </Link>
        </div>

        {/* Site Switcher */}
        <div className="p-3 border-b border-sidebar-border">
          <Select
            value={siteSlug}
            onValueChange={(value: string) => {
              if (value && value !== siteSlug) window.location.href = `/sites/${value}/posts`;
            }}
          >
            <SelectTrigger className="sidebar-site-switch w-full bg-sidebar-accent" aria-label={`Switch site: ${siteName ?? siteSlug}`} title={`Switch site: ${siteName ?? siteSlug}`}>
              <Globe aria-hidden="true" className="sidebar-compact-icon hidden size-4 shrink-0" />
              {/* base-ui Value renders the RAW value (the slug) without children —
                  always show the display name (Rick: "avoid slug"). */}
              <span className="sidebar-label"><SelectValue>{siteName ?? siteSlug}</SelectValue></span>
            </SelectTrigger>
            <SelectContent>
              {sites.map((s) => (
                // The current site is shown (with its check) but not pickable.
                <SelectItem key={s.pid} value={s.slug} disabled={s.slug === siteSlug} className="data-[disabled]:opacity-100">
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {/* AB#820: the deploy pill shares this row. Collapsed rail keeps both —
              the button's label is the thing that goes sr-only, not the pill. */}
          <div className="mt-2 flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={openPreview}
              disabled={previewBuilding}
              aria-busy={previewBuilding}
              title="Preview site"
              className="sidebar-action min-w-0 flex-1 justify-start text-sidebar-muted hover:bg-sidebar-accent hover:text-sidebar-foreground"
            >
              <ExternalLink data-anim="nudge" className="size-4" />
              <span className="sidebar-label">{previewBuilding ? 'Building preview…' : 'Preview site'}</span>
            </Button>
            <DeployBadge siteSlug={siteSlug} />
          </div>
        </div>

        <nav id="site-navigation" aria-label="Site navigation" className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
          {navItems.map((item) => {
            const domain = sidebarDomain(item.to.replace('$siteSlug', siteSlug), siteSlug);
            const notice = domain ? noticeForSection(domain) : undefined;
            const active = location.pathname.startsWith(
              `/sites/${siteSlug}/${item.label.toLowerCase()}`
            );
            return (
              <Link
                key={item.to}
                title={item.label}
                to={item.to}
                params={{ siteSlug }}
                className={navItemClass(active, notice)}
              >
                <NavDot active={active} />
                <item.icon aria-hidden="true" data-anim={item.anim} className="size-4 shrink-0" />
                <NavLabel sidebarOpen={sidebarOpen} label={item.label} notice={notice} />
              </Link>
            );
          })}

          {/* Custom Types with sub-menu */}
          <div>
            <Link
              title="Custom Types"
              to="/sites/$siteSlug/content-types"
              params={{ siteSlug }}
              className={navItemClass(isCustomTypesRoute, noticeForSection('content-types'))}
            >
              <NavDot active={isCustomTypesRoute} />
              <Shapes aria-hidden="true" data-anim="pop" className="size-4 shrink-0" />
              <NavLabel sidebarOpen={sidebarOpen} label="Custom Types" notice={noticeForSection('content-types')} />
            </Link>
            {isCustomTypesRoute && (
              <div className="sidebar-submenu pl-4" ref={revealSubmenu}>
                {customTypesItems.map((item) => {
                  const active =
                    location.pathname ===
                    `/sites/${siteSlug}/content-types${item.to.includes('tables') ? '/tables' : ''}`;
                  return (
                    <Link
                      key={item.to}
                      title={item.label}
                      to={item.to}
                      params={{ siteSlug }}
                      activeOptions={item.exact ? { exact: true } : undefined}
                      className={subItemClass(active)}
                    >
                      <NavDot active={active} />
                      <item.icon aria-hidden="true" className="size-4 shrink-0" />
                      <span className="sidebar-label">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>

          {(() => {
            const active = location.pathname.startsWith(`/sites/${siteSlug}/platforms`);
            const category = (location.search as { category?: string }).category ?? 'social';
            return <div>
              <Link title="Site platforms" to="/sites/$siteSlug/platforms" params={{ siteSlug }} search={{ category: 'social', view: 'assigned' }} className={navItemClass(active, noticeForSection('platforms'))}>
                <NavDot active={active} /><Plug aria-hidden="true" data-anim="lift" className="size-4 shrink-0" /><NavLabel sidebarOpen={sidebarOpen} label="Site platforms" notice={noticeForSection('platforms')} />
              </Link>
              {active ? <div className="sidebar-submenu pl-4">{([{ id: 'social', label: 'Social', icon: Megaphone }, { id: 'deployment', label: 'Deploy', icon: ExternalLink }, { id: 'storage', label: 'Storage', icon: Database }] as const).map((item) => <Link key={item.id} title={item.label} to="/sites/$siteSlug/platforms" params={{ siteSlug }} search={{ category: item.id, view: 'assigned' }} className={subItemClass(category === item.id)} aria-current={category === item.id ? 'page' : undefined}><NavDot active={category === item.id} /><item.icon aria-hidden="true" className="size-4 shrink-0" /><span className="sidebar-label">{item.label}</span></Link>)}</div> : null}
            </div>;
          })()}

          {/* Settings with sub-menu */}
          <div>
            <Link
              title="Settings"
              to="/sites/$siteSlug/settings"
              params={{ siteSlug }}
              className={navItemClass(isSettingsRoute, noticeForSection('settings'))}
            >
              <NavDot active={isSettingsRoute} />
              <Settings2 aria-hidden="true" data-anim="tune" className="size-4 shrink-0" />
              <NavLabel sidebarOpen={sidebarOpen} label="Settings" notice={noticeForSection('settings')} />
            </Link>
            {isSettingsRoute && (
              <div className="sidebar-submenu pl-4" ref={revealSubmenu}>
                {settingsSubItems.map((item) => {
                  const resolved = item.to.replace('$siteSlug', siteSlug);
                  const active = item.exact
                    ? location.pathname === resolved
                    : location.pathname.startsWith(resolved);
                  return (
                    <Link
                      key={item.to}
                      title={item.label}
                      to={item.to}
                      params={{ siteSlug }}
                      activeOptions={item.exact ? { exact: true } : undefined}
                      className={subItemClass(active)}
                    >
                      <NavDot active={active} />
                      <item.icon aria-hidden="true" className={cn('size-4 shrink-0', item.fill && 'fill-current')} />
                      <span className="sidebar-label">{item.label}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </nav>

        <div className="sidebar-footer p-3 border-t border-sidebar-border space-y-3">
          <Link
            to="/sites"
            title="All sites"
            className="sidebar-action flex items-center justify-center gap-2 px-3 py-2 text-center bg-sidebar-accent rounded-md text-sidebar-muted hover:text-sidebar-foreground text-sm no-underline outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50"
          >
            <Globe aria-hidden="true" className="sidebar-compact-icon hidden size-4" /><span className="sidebar-label">All Sites</span>
          </Link>
          <button
            type="button"
            aria-label={baxterOpen ? 'Close Baxter' : 'Open Baxter, your AI co-editor'}
            aria-expanded={baxterOpen}
            onClick={() => toggleBaxter(!baxterOpen)}
            title="Baxter AI"
            // AB#929: while the rail is open the button wears the nav's own
            // active-item treatment, so "Baxter is on" reads at a glance.
            className={cn(
              'sidebar-action flex w-full items-center justify-center gap-2 px-3 py-2 bg-sidebar-accent rounded-md text-sidebar-muted hover:text-sidebar-foreground text-sm outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50',
              baxterOpen && 'text-sidebar-foreground font-medium ring-1 ring-primary/50',
            )}
          >
            <BaxterMark className="size-[50px]" />
            <span className="sidebar-label">Baxter AI</span>
            {/* AB#943: hollow while Baxter is closed (twinkles on hover), filled once open. */}
            <Sparkles data-anim="twinkle" className={cn('sidebar-label size-4 shrink-0 text-primary', baxterOpen && 'fill-current')} aria-hidden="true" />
          </button>
          <AccountMenu
            side="right"
            align="end"
            trigger={
              <button
                type="button"
                title="Account"
                className="sidebar-action flex items-center justify-center gap-2 w-full px-3 py-2 text-center bg-sidebar-accent rounded-md text-sidebar-muted hover:text-sidebar-foreground text-sm outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50"
              >
                <UserRound aria-hidden="true" className="sidebar-compact-icon hidden size-4" /><span className="sidebar-label">Account</span>
              </button>
            }
          />
          <div className="sidebar-label text-sidebar-muted text-sm truncate">{user?.email}</div>
          {/* Same quiet treatment as its siblings: logging out loses nothing. */}
          <button
            type="button"
            title="Log out"
            className="sidebar-logout sidebar-action flex items-center justify-center gap-2 w-full px-3 py-2 text-center bg-sidebar-accent rounded-md text-sidebar-muted hover:text-sidebar-foreground text-sm outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50"
            onClick={() => logout()}
          >
            <LogOut aria-hidden="true" className="sidebar-compact-icon hidden size-4" /><span className="sidebar-label">Log out</span>
          </button>
          <div className="sidebar-theme-row flex justify-center">
            <ThemeToggle className="sidebar-theme" />
          </div>
        </div>
      </aside>

      <main
        style={panelStyle}
        className={cn(
          'site-main flex-1 min-w-0 md:ml-60 p-6 pt-20 md:pt-6 bg-background min-h-screen',
          baxterOpen && (baxterDock === 'right' ? 'md:mr-[var(--baxter-width)]' : 'md:mb-[var(--baxter-height)]'),
        )}
      >
        <div className="flex items-center gap-1.5 mb-6 text-sm">
          <Link to="/sites" className="text-muted-foreground hover:text-foreground transition-colors">Sites</Link>
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50 shrink-0" />
          <span className="text-foreground font-medium truncate">{site.name}</span>
        </div>
        <Outlet />
      </main>

      {baxterOpen && (
        <>
          {/* Below md the rail is a drawer over the page, like the nav one. */}
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/50 transition-opacity duration-200 ease-out starting:opacity-0"
            aria-hidden
            onClick={() => toggleBaxter(false)}
          />
          <aside
            aria-label="Baxter, your AI co-editor"
            data-dock={baxterDock}
            style={{ ...panelStyle, borderColor: 'var(--primary)' }}
            // Below md it is always the right-hand drawer; the dock only applies
            // from md up, where there is room for a bottom rail beside the nav.
            className={cn(
              'baxter-rail fixed inset-y-0 right-0 z-50 w-[24rem] max-w-full border-l-[3px] bg-card md:w-[var(--baxter-width)]',
              // Below md it slides in from the edge like the nav drawer; docked from md up it never moves.
              'max-md:transition-transform max-md:duration-200 max-md:ease-drawer max-md:starting:translate-x-full motion-reduce:transition-none',
              baxterDock === 'bottom' &&
                'md:inset-x-0 md:left-60 md:top-auto md:h-[var(--baxter-height)] md:w-auto md:border-l-0 md:border-t-[3px]',
            )}
          >
            {!baxterCollapsed && <div
              role="separator"
              aria-label={baxterDock === 'right' ? 'Resize Baxter width' : 'Resize Baxter height'}
              aria-orientation={baxterDock === 'right' ? 'vertical' : 'horizontal'}
              aria-valuemin={baxterDock === 'right' ? 320 : 240}
              aria-valuemax={maxSize}
              aria-valuenow={size}
              tabIndex={0}
              className={cn('hidden md:block absolute z-10 bg-border hover:bg-primary focus-visible:bg-primary outline-none', baxterDock === 'right' ? 'inset-y-0 left-0 w-1 cursor-ew-resize' : 'inset-x-0 top-0 h-1 cursor-ns-resize')}
              onPointerDown={(event) => { event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId); dragStart.current = { position: baxterDock === 'right' ? event.clientX : event.clientY, size }; }}
              onPointerMove={(event) => { if (dragStart.current) resize(dragStart.current.size + dragStart.current.position - (baxterDock === 'right' ? event.clientX : event.clientY)); }}
              onPointerUp={(event) => { dragStart.current = null; event.currentTarget.releasePointerCapture(event.pointerId); }}
              onPointerCancel={() => { dragStart.current = null; }}
              onKeyDown={(event) => {
                const delta = baxterDock === 'right' ? event.key === 'ArrowLeft' ? 20 : event.key === 'ArrowRight' ? -20 : 0 : event.key === 'ArrowUp' ? 20 : event.key === 'ArrowDown' ? -20 : 0;
                if (delta) { event.preventDefault(); resize(size + delta); }
              }}
            />}
            {baxterCollapsed && <div className={cn('hidden md:flex items-center gap-1 p-1', baxterDock === 'right' ? 'flex-col' : 'flex-row')}>
              <Button variant="ghost" size="icon-sm" aria-label="Expand Baxter" onClick={() => collapseBaxter(false)}><BaxterMark className="size-7" /></Button>
              <Button variant="ghost" size="icon-sm" aria-label="New Baxter chat" onClick={() => runRailAction('new')}><Plus /></Button>
              <Button variant="ghost" size="icon-sm" aria-label="Baxter chat history" onClick={() => runRailAction('history')}><History /></Button>
            </div>}
            <div className={baxterCollapsed ? 'h-full md:hidden' : 'h-full'}>
            <BaxterPanel
              // Switching sites keeps this route mounted, so remount on the slug:
              // the chat (and the chat it restores) belongs to ONE site's DB.
              key={siteSlug}
              siteSlug={siteSlug}
              onClose={() => toggleBaxter(false)}
              dock={baxterDock}
              onToggleDock={toggleBaxterDock}
              onCollapse={() => collapseBaxter(true)}
              railAction={railAction}
            />
            </div>
          </aside>
        </>
      )}
    </div>
  );
}
